export type Json = {}

export type APIProfile = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  secret: string
  token: string
  tokenExpirationDate: Date | null
  companyId: string | null
}

export type AccountableAccount = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  description: string
  shortDescription: string | null
  type: AccountableAccount_type | null
  displayName: string | null
  tagId: string | null
  subAccountableAccountOfId: string | null
  companyId: string | null
  suffix: string | null
  name1: string
  name2: string
  name3: string
  name4: string
  name5: string
  name6: string
  user: boolean | null
  thirdPartyCompany: boolean | null
  bankAccount: boolean | null
  publish: boolean
  accountableAccountTableId: string | null
  lettering: boolean | null
  obsolete: boolean
  detailedCarryForward: boolean | null
  vatRate: number | null
  vatType: VAT_type | null
  vatOperationType: VAT_operationType | null
  transactionType: BankTransaction_transactionType | null
  default: boolean | null
  paidAndUncashed: boolean | null
}

export type AccountableAccountTable = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  companyScopeId: string | null
  companyId: string | null
  parentId: string | null
  aaCharacterCount: number
}

export type AccountableAccountMapping = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  description: string | null
  accountableAccountId: string | null
  accountingFirmId: string | null
}

export type AccountableAccountRule = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  lettering: boolean | null
  accountableAccountId: string | null
  accountingFirmId: string | null
}

export type AccountableAccountTag = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  tagId: string | null
  accountableAccountId: string | null
  partitionId: string | null
}

export type AccountingClosing = {
  id: string
  createdAt: Date | null
  name: string
  endDate: Date | null
  accountingPeriodId: string | null
}

export type AccountingFirmClient = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  startProductionDate: Date | null
  endProductionDate: Date | null
  archivingDate: Date | null
  accountingAvailabilityDate: Date | null
  freeMonthCount: number | null
  accountingFirmId: string | null
  clientId: string | null
  accountManagerId: string | null
  accountingSignatoryId: string | null
  offer: Offer_type
}

export type AccountingFirmClientPrice = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  date: Date | null
  free: boolean | null
  invoiceId: string | null
  accountingFirmClientId: string | null
  productId: string | null
  price: number | null
  offer: Offer_type | null
  data: Json | null
}

export type BobbeePrice = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  priceListId: string
  invoiceProductId: string | null
  unitPrice: number | null
  accountingType: ProfileCompany_accountingType | null
  taxSystem: Company_taxSystem | null
  withVAT: boolean | null
  minAccountingLineCount: number | null
  maxAccountingLineCount: number | null
  discountPercentage: number | null
  discount: number | null
  offer: Offer_type
  maxConnectorCount: number | null
  additionnalConnectorPrice: number | null
  additionnalAccountingLinePrice: number | null
  maxUserCount: number | null
  additionalUserPrice: number | null
  minQuantity: number | null
}

export type BobbeePriceList = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string | null
  type: BobbeePriceList_type
  scope: BobbeePriceList_scope
  deprecated: boolean
  discountPercentage: number | null
  discount: number | null
  discountDuration: number | null
  version: number | null
}

export type AccountingItemExport = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  status: AccountingItemExport_status
  step: number | null
  stepCount: number | null
  errors: Json | null
  companyId: string | null
  accountingPeriodId: string | null
  fileId: string | null
  type: AccountingItemExport_type | null
  startDate: Date | null
  endDate: Date | null
  accountingClosingId: string | null
  notDeletable: boolean | null
}

export type AccountingItemImport = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  status: AccountingItemImport_status
  step: number | null
  stepCount: number | null
  entryCount: number | null
  data: Json | null
  errors: Json | null
  companyId: string | null
  accountingPeriodId: string | null
  fileId: string | null
  origin: string | null
  application: string | null
  type: AccountingItemImport_type | null
  importId: string | null
  zipFileId: string | null
  owner: boolean
  startDate: Date | null
  endDate: Date | null
}

export type AccountingLine = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  originalAccountableAccount: string | null
  wording: string | null
  amount: number | null
  amountSigned: number | null
  date: Date | null
  originalDate: Date | null
  index: number | null
  debit: number | null
  credit: number | null
  useForReconciliation: boolean
  reconciliationId: string | null
  accountableAccountId: string | null
  bankAccountId: string | null
  documentId: string | null
  accountingPeriodId: string | null
  userId: string | null
  bankTransactionId: string | null
  thirdPartyCompanyId: string | null
  type: AccountingLine_type
  budgetId: string | null
  tagId: string | null
  budgetSecondaryKeys: string | null
  companyId: string | null
  dueDate: Date | null
}

export type AccountingPeriod = {
  id: string
  createdAt: Date | null
  start: Date | null
  end: Date | null
  companyId: string | null
  closed: boolean
  amountSubsciption: number | null
  temporaryClosed: boolean
  documentExportDicoCounter: Json | null
  personnalisedTableData: Json | null
  lastCarryForwardComputation: Date | null
  isAccountingSituationOfId: string | null
}

export type AccountingReview = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  companyId: string | null
  accountingPeriodId: string | null
  layoutId: string | null
}

export type AccountingReviewLayout = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  forSituation: boolean
  layout: Json | null
  accountingFirmId: string | null
  companyScopeId: string | null
}

export type ApplicationSettings = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  maintenance: boolean | null
}

export type AuditCheck = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  key: string
  type: AuditCheck_type | null
  scope: string | null
  accessRight: number
  status: AuditCheck_status
  title: string | null
  form: Json | null
  accountingFirmId: string | null
  data: Json | null
}

export type AuditCheckResult = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  key: string
  status: AuditCheckResult_status | null
  reviewed: number | null
  obsolescenceDate: Date | null
  data: Json | null
  auditCheckId: string | null
  companyId: string | null
  bankTransactionId: string | null
  expenseReportId: string | null
  bankAccountId: string | null
  accountingPeriodId: string | null
  documentId: string | null
  zTillReceiptId: string | null
  thirdPartyCompanyId: string | null
  accountingLineId: string | null
  type: AuditCheckResult_type | null
}

export type Bank = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  budgetInsightId: number | null
}

export type BankAccount = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: BankAccount_type
  name: string
  budgetInsightId: number | null
  balance: number | null
  iban: string | null
  creditorIdentifier: string | null
  currency: BankAccount_currency | null
  number: string | null
  lastReconnection: Date | null
  initialSyncDone: boolean | null
  currentError: string | null
  btIndice: number | null
  accountableAccountIndice: number | null
  userId: string | null
  bankId: string | null
  companyId: string | null
  importId: string | null
  bankAccountGroupId: string | null
  displayName: string | null
  bic: string | null
  externalName: string | null
  importStartDate: Date | null
  startAuthorizedDate: Date | null
  budgetInsightWebsiteField: string | null
}

export type BankAccountBalance = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  date: Date | null
  balance: number | null
  expenses: number | null
  incomes: number | null
  accountId: string | null
  type: BankAccountBalance_type | null
  use: boolean | null
}

export type BankTransaction = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  budgetInsightId: number | null
  internalId: string | null
  status: BankTransaction_status
  analysis: Json | null
  amount: number | null
  notJustifiedAmountSigned: number | null
  dateDebit: Date | null
  dateRealization: Date | null
  wordingOriginal: string | null
  wordingSimplified: string | null
  wordingLight: string | null
  wordingLightHash: string | null
  card: string | null
  nature: string | null
  type: string | null
  description: string | null
  transactionType: BankTransaction_transactionType | null
  wordingDisplay: string | null
  importId: string | null
  accountId: string | null
  reconciliationId: string | null
  accountingItemImportId: string | null
  accountableAccountId: string | null
  importDocumentId: string | null
  justifiedAmountSigned: number | null
  version: number
  accountingClosingId: string | null
  vatDeclarationId: string | null
  uniqueExportId: string | null
}

export type BankTransactionWordingPattern = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string | null
  patternSpec: Json | null
  pattern: string
  minAmount: number | null
  maxAmount: number | null
  outputWording: string | null
  zTillReceipt: boolean | null
  withoutInvoice: boolean | null
  salesInvoiceAuto: boolean | null
  thirdPartyOnly: boolean | null
  withoutInvoiceWithThirdParty: boolean | null
  vatRate: number | null
  vatType: VAT_type | null
  accountableAccountId: string | null
  thirdPartyAccountableAccountId: string | null
  thirdPartyCompanyId: string | null
  thirdPartyUserId: string | null
  tags: string | null
  rules: Json | null
  companyScopeId: string | null
  scopeSharingStatus: BankTransactionWordingPattern_scopeSharingStatus | null
  clientCount: number
  activityCount: number
  taxSystems: string | null
  accountingCompanyId: string | null
  accountingCompanyOwnerId: string | null
  ownerId: string | null
}

export type Budget = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  date: Date | null
  type: Budget_type | null
  form: Json | null
  companyId: string | null
  partitionTagId: string | null
  formText: string | null
  formNM1: Json | null
  parameters: Json | null
  groupingMode: string | null
}

export type BudgetFormLine = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: BudgetFormLine_type | null
  data: Json | null
  accountableAccountId: string | null
  thirdPartyCompanyId: string | null
  tagId: string | null
  userId: string | null
  budgetId: string | null
}

export type Chat = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  reconciliationId: string | null
  lastMessageId: string | null
  documentId: string | null
  expenseReportId: string | null
  companyId: string | null
  bankTransactionId: string | null
  type: Chat_type
  auditCheckAccountingPeriodKey: string | null
  processed: boolean | null
}

export type ChatMessage = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  text: string | null
  starred: boolean
  smartAsk: ChatMessage_smartAsk | null
  senderId: string | null
  chatId: string | null
  replyMessageId: string | null
}

export type ChatUser = {
  id: string
  unreadMessageCount: number | null
  chatId: string | null
  userId: string | null
}

export type Company = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: Company_type
  solution: Company_solution | null
  name: string | null
  shortName: string | null
  siret: string | null
  siren: string | null
  vatNumber: string | null
  registrationTown: string | null
  address: string | null
  addressComplement: string | null
  postalCode: string | null
  city: string | null
  country: string | null
  accountingFirm: boolean | null
  isAccountingFirm: boolean | null
  legalForm: Company_legalForm | null
  legalFormCategory: number | null
  isActive: boolean
  declareSalesInvoices: boolean
  vatSystem: Company_vatSystem | null
  taxSystem: Company_taxSystem | null
  incomeCategory: Company_incomeCategory | null
  taxRegime: Company_taxRegime | null
  subjectToVAT: boolean | null
  vehicle: boolean | null
  salaryCount: number | null
  withManager: boolean | null
  shop: boolean | null
  shareCapital: number | null
  shareTotal: number | null
  invoiceCounter: number
  quoteCounter: number
  deliveryBillCounter: number
  creditNoteCounter: number
  depositCounter: number
  externalId: string | null
  invoiceData: Json | null
  invoiceTextPaymentDetails: string | null
  invoiceTextLegalNotices: string | null
  invoiceTextPaymentMode: string | null
  invoiceNumbering: Company_invoiceNumbering | null
  invoiceSequenceStart: number | null
  categories: string | null
  logoId: string | null
  parentCompanyId: string | null
  invoiceNumberPatternId: string | null
  taxSystemType: Company_taxSystemType | null
  legalUnitName: string | null
  corporateTaxDepartment: string | null
  startBusinessDate: Date | null
  firstAccountingPeriodEnd: Date | null
  tradeName: string | null
  budgetInsightId: number | null
  budgetInsightToken: string | null
  vatDeclarationDay: number | null
  groupKey: string | null
  lastReconciliationDate: Date | null
  thirdPartyCompanyData: Json | null
  timeZone: string | null
  thirdPartyCompanyPaymentType: Company_thirdPartyCompanyPaymentType | null
  vatOption: Company_vatOption | null
  buildings: Json | null
  enableAccountingFirms: boolean | null
  isAccountingFirmOfId: string | null
  clientUserAccessRight: Company_clientUserAccessRight | null
  bobbeePriceId: string | null
  bobbeePriceListId: string | null
  rfRevenueType: Company_rfRevenueType | null
  agirisId: string | null
}

export type AccountingWorkspace = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  accountingFirmId: string | null
}

export type CompanyActivity = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  description: string
}

export type CompanyActivitySuggestion = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  status: CompanyActivitySuggestion_status | null
  count: number | null
  activityId: string | null
  tagId: string | null
  purchaseAccountId: string | null
  salesAccountId: string | null
}

export type CompanyScope = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string | null
  scopeSharingStatus: CompanyScope_scopeSharingStatus | null
  clientCount: number
  activityCount: number
  taxSystems: string | null
  accountingCompanyId: string | null
  accountingCompanyOwnerId: string | null
  ownerId: string | null
}

export type CompanySuggestion = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  status: CompanySuggestion_status | null
  count: number | null
  companyId: string | null
  purchaseAccountId: string | null
  salesAccountId: string | null
}

export type CompanyUser = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  investment: Json | null
  profileId: string | null
  companyId: string | null
  userId: string | null
  role: CompanyUser_role | null
  companyOwnerType: CompanyUser_companyOwnerType | null
  legalRepresentativeType: CompanyUser_legalRepresentativeType | null
  accountingLegalRepresentative: boolean | null
  configuration: Json | null
  accountableAccountIndice: number | null
  partOfCompany: boolean
}

export type ComputedIndicator = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  companyScopeId: string | null
  definition: Json | null
}

export type DashboardSetting = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  setting: Json | null
  userId: string | null
  companyId: string | null
}

export type Declaration = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  status: Declaration_status | null
  formKey: string | null
  data: Json | null
  mtaeStatus: string | null
  mtaeId: string | null
  date: Date | null
  startDate: Date | null
  endDate: Date | null
  documentId: string | null
  formId: string | null
  companyId: string | null
  mtaeData: Json | null
  mtaeCode: number | null
  mtaeResponseCode: string | null
  isSheetOfId: string | null
  amountPaid: number | null
  amountDeclared: number | null
  accountingPeriodId: string | null
  type: Declaration_type | null
  isBundleSheetOfId: string | null
  bookletTemplateId: string | null
  vatDeclarationId: string | null
}

export type DeclarationForm = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  key: string
  year: string
  name: string
  form: Json | null
  cerfaNumber: string | null
  version: number
  type: DeclarationForm_type
  status: DeclarationForm_status
  availabilityDate: Date | null
}

export type Document = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  travelExpense: Json | null
  name: string | null
  displayName: string | null
  type: Document_type | null
  show: boolean
  multiVat: boolean | null
  status: Document_status | null
  creditNote: boolean
  vatType: VAT_type | null
  errorMessage: string | null
  importId: string | null
  useForReconciliation: boolean
  dueDate: Date | null
  edmLastExportedDate: Date | null
  accountingLinesLocked: boolean
  analysis: Json | null
  analysisFileId: string | null
  data: Json | null
  vatRate1Auto: number | null
  vatRate1: number | null
  vatAmount1Auto: number | null
  vatAmount1: number | null
  netAmount1Auto: number | null
  netAmount1: number | null
  grossAmount1Auto: number | null
  grossAmount1: number | null
  vatRate2Auto: number | null
  vatRate2: number | null
  vatAmount2Auto: number | null
  vatAmount2: number | null
  netAmount2Auto: number | null
  netAmount2: number | null
  grossAmount2Auto: number | null
  grossAmount2: number | null
  vatRate3Auto: number | null
  vatRate3: number | null
  vatAmount3Auto: number | null
  vatAmount3: number | null
  netAmount3Auto: number | null
  netAmount3: number | null
  grossAmount3Auto: number | null
  grossAmount3: number | null
  vatRateAuto: number | null
  vatRate: number | null
  netAmountAuto: number | null
  netAmount: number | null
  netAmountSigned: number | null
  grossAmountAuto: number | null
  grossAmount: number | null
  grossAmountSigned: number | null
  vatAmountAuto: number | null
  vatAmount: number | null
  vatAmountSigned: number | null
  freeAmount: number | null
  dateAuto: Date | null
  date: Date | null
  invoiceNumberAuto: string | null
  invoiceNumber: string | null
  bankTransactionId: string | null
  invoiceId: string | null
  stockInventoryId: string | null
  ownerId: string | null
  originalId: string | null
  accountableAccountAutoId: string | null
  thirdPartyAccountableAccountId: string | null
  reconciliationId: string | null
  closureCreditNoteId: string | null
  equalExpenseReportId: string | null
  workingCopyId: string | null
  isVatDocumentOfId: string | null
  companyId: string | null
  accountingItemImportId: string | null
  thirdPartyCompanyId: string | null
  zTillReceiptId: string | null
  expenseReportId: string | null
  vatDeclarationId: string | null
  accountableAccountId: string | null
  userId: string | null
  paymentDate: Date | null
  ocrId: string | null
  netAmountAccountableAccountDefined: boolean | null
  fromDocumentId: string | null
  lastExportDate: Date | null
  isResultOfId: string | null
  uniqueExportId: string | null
  accountingDate: Date | null
  lastDateSendedByMail: Date | null
  lastSentMailId: string | null
  accountingClosingId: string | null
}

export type DocumentRule = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string | null
  conditions: Json | null
  accountableAccountId: string | null
  thirdPartyAccountableAccountId: string | null
  thirdPartyCompanyId: string | null
  companyScopeId: string | null
  tags: string | null
  rules: Json | null
}

export type DocumentUpdateStatus = {
  id: string
  lastBuildDate: Date | null
  errors: Json | null
}

export type EngagementLetter = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  data: Json | null
  status: EngagementLetter_status
  signatureId: string | null
  accountantId: string | null
  companyId: string | null
  prospectedCompanyId: string | null
  documentId: string | null
}

export type ExpenseReport = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  importId: string | null
  amount: number | null
  status: ExpenseReport_status | null
  date: Date | null
  dateAuto: Date | null
  userId: string | null
  companyId: string | null
  accountingItemImportId: string | null
  lastExportDate: Date | null
  uniqueExportId: string | null
}

export type File = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  url: string | null
  key: string
  type: File_type | null
  fileSize: number | null
  height: number | null
  width: number | null
  sha256: string | null
  bookletTemplateId: string | null
}

export type TestFile = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  key: string
  type: File_type | null
  name: string
}

export type ForbiddenReconciliation = {
  id: string
  key: string
}

export type InvoiceNumberPattern = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  text: string | null
  regexp: string | null
  yearPosition: number | null
  monthPosition: number | null
  dayPosition: number | null
  sequencePosition: number | null
}

export type UserSubscription = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  userId: string
  subscription: User_SubscriptionType | null
  companyId: string
  isSubscribe: boolean
}

export type Mail = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  importId: string | null
  subject: string
  text: string
  textAsHtml: string | null
  from: string
  to: string
  date: Date | null
  lastReadDate: Date | null
  fileId: string | null
  companyId: string | null
  accountingCompanyId: string | null
  shortText: string | null
  thirdPartyCompanyId: string | null
  data: Json | null
  type: Mail_type
}

export type ManualUserActivity = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  date: Date | null
  duration: number | null
  accountingPeriodId: string | null
  userId: string | null
  companyId: string | null
}

export type OperatingAccount = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  data: Json | null
  use: OperatingAccount_use
  type: OperatingAccount_type
  status: OperatingAccount_status
  clientCount: number
  activityCount: number
  taxSystems: string | null
  accountingCompanyId: string | null
  ownerId: string | null
  accountingCompanyOwnerId: string | null
  scopeSharingStatus: OperatingAccount_scopeSharingStatus | null
  companyScopeId: string | null
}

export type PartitionTag = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: PartitionTag_type | null
  uniqueName: string | null
  name: string
  companyId: string | null
  scope: PartitionTag_scope
  rules: Json | null
  companyScopeId: string | null
  parentId: string | null
}

export type Payment = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: Payment_type
  budgetInsightId: number | null
  uuid: string | null
  label: string
  amountSigned: number | null
  paymentCreditCount: number | null
  addedPaymentCreditCount: number | null
  removedPaymentCreditCount: number | null
  status: Payment_status | null
  errorCode: string | null
  thirdPartyCompanyId: string | null
  companyId: string | null
  userId: string | null
  resultDocumentId: string | null
  data: Json | null
}

export type Profile = {
  id: string
  name: string | null
  type: Profile_type | null
  restrictionLevel: number | null
  homePage: string | null
  configuration: Json | null
  title: string | null
  companyId: string | null
  test: boolean | null
  root: boolean | null
  free: boolean
  help: boolean | null
  menuDocuments: boolean | null
  menuBank: boolean | null
  menuBudgets: boolean | null
  menuAccountingItem: boolean | null
  menuZTillReceipt: boolean | null
  menuSalesInvoice: boolean | null
  menuConfiguration: boolean | null
  menuAudit: boolean | null
  documentRead: boolean | null
  documentWrite: boolean | null
  documentExpenseReportWrite: boolean | null
  documentTaggingWrite: boolean | null
  documentOnlySelf: boolean | null
  documentStatusDisplay: boolean | null
  purchaseInvoiceImport: boolean | null
  salesInvoiceImport: boolean | null
  multiSalesInvoiceImport: boolean | null
  currentAccountImport: boolean | null
  paySlipInvoiceImport: boolean | null
  expenseReportRead: boolean | null
  expenseReportReadOnlySelf: boolean | null
  expenseReportWrite: boolean | null
  expenseReportWriteOnlySelf: boolean | null
  expenseReportValidateWrite: boolean | null
  accountingItemRead: boolean | null
  accountingItemWrite: boolean | null
  zTillReceiptRead: boolean | null
  zTillReceiptWrite: boolean | null
  salesInvoiceRead: boolean | null
  salesInvoiceWrite: boolean | null
  budgetRead: boolean | null
  budgetWrite: boolean | null
  budgetImport: boolean | null
  budgetInsight: boolean | null
  bankAccountCreate: boolean | null
  bankAccountDelete: boolean | null
  bankTransactionImport: boolean | null
  bankTransactionRead: boolean | null
  bankTransactionAccounting: boolean | null
  reconciliation: boolean | null
  dashboardCustomer: boolean | null
  dashboardAccounting: boolean | null
  newsFeedAdmin: boolean | null
  newsFeedEmployee: boolean | null
  toBeDone: boolean | null
  exportAccounting: boolean | null
  vatDeclaration: boolean | null
  settingsRead: boolean | null
  settingsWrite: boolean | null
  admin: boolean | null
  chatRead: boolean | null
  chatWrite: boolean | null
  chatOnlySelf: boolean | null
  wordingAnalysisRead: boolean | null
  wordingAnalysisWrite: boolean | null
  wordingAnalysisWriteRestricted: boolean | null
  menuAccountingPeriod: boolean
  menuGeneralLedger: boolean
  menuIncomeStatementSubtotal: boolean
  accountingRoot: boolean | null
  menuOpenPoints: boolean
  originalProfileId: string | null
  displayName: string | null
}

export type ProfileCompany = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  createZTillReceipt: boolean | null
  createExpenseReport: boolean | null
  createPaySlip: boolean | null
  stripeManual: boolean | null
  stripeAuto: boolean | null
  stripeConfig: Json | null
  mollieManual: boolean | null
  mollieAuto: boolean | null
  mollieConfig: Json | null
  taxeApprentissage: boolean | null
  taxeSurLesSalaires: boolean | null
  contributionUniqueALaFormation: boolean | null
  contributionEffortConstruction: boolean | null
  companyId: string | null
  gmail: boolean | null
  gmailConfig: Json | null
  accountingMail: boolean | null
  accountingMailConfig: Json | null
  documentFromMail: boolean | null
  documentFromMailConfig: Json | null
  vatDeclarationAuto: boolean | null
  zTillReceiptWithTransactions: boolean | null
  vatBankAccountId: string | null
  accountingType: ProfileCompany_accountingType | null
  jeDeclareDotCom: boolean | null
  jeDeclareDotComConfig: Json | null
  sendingMailSMTP: boolean | null
  sendingMailSMTPConfig: Json | null
  rofs: Json | null
  taxIntegration: boolean | null
  ogaEnrolment: boolean | null
  ogaEnrolmentConfig: Json | null
  paymentCreditCount: number
  useOtherRules: boolean
  createSalesInvoice: boolean
  soBankConfig: Json | null
  activateTask: boolean
  autoTaxTask: boolean | null
  autoVATTask: boolean | null
  soBank: boolean | null
  workflow: Json | null
  stock: boolean | null
  stockConfig: Json | null
  managementAuthorizations: Json | null
  managementAuthorization: boolean | null
  mailCollectKey: string | null
  mailCollect: boolean | null
}

export type Reconciliation = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  exportedAt: Date | null
  status: Reconciliation_status
  type: Reconciliation_type
  toReplaceDocumentType: Reconciliation_toReplaceDocumentType | null
  companyId: string | null
  accountingItemImportId: string | null
  date: Date | null
}

export type PartialTaxPayment = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: Tax_type
  totalVatAmount: number | null
  details: Json | null
  vatDeclarationId: string | null
  documentId: string | null
  paymentId: string | null
}

export type Tag = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  uniqueName: string | null
  name: string
  shortName: string | null
  type: Tag_type | null
  partitionTagId: string | null
  companyId: string | null
  percentages: Json | null
  isPercentageTagOfId: string | null
  primaryLog: Document_type | null
  invoiceProductType: InvoiceProduct_type | null
  data: Json | null
}

export type Task = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  periodicity: Task_periodicity | null
  start: Date | null
  end: Date | null
  type: Task_type
  status: Task_status
  title: string
  description: string | null
  dueDate: Date | null
  auto: boolean | null
  recurringTaskId: string | null
  clientId: string | null
  ownerId: string | null
  companyId: string | null
}

export type ThirdPartyCompanyData = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  data: Json | null
  thirdPartyCompanyId: string | null
  companyId: string | null
}

export type ThirdPartyCompanyTag = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  thirdPartyCompanyId: string | null
  tagId: string | null
  companyId: string | null
}

export type User = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: User_type
  email: string | null
  emailSubscription: string | null
  emailVerified: boolean
  emailVerificationToken: string | null
  password: string | null
  mailSubscription: boolean
  lastSentMailDate: Date | null
  firstname: string | null
  lastname: string | null
  resetPasswordToken: string | null
  tokenExpirationDate: Date | null
  budgetInsightId: number | null
  budgetInsightToken: string | null
  lastAppVersion: string | null
  companyId: string | null
  profileId: string | null
  firstLogin: boolean
  hourlyCost: number | null
  birthDate: Date | null
  birthDepartment: string | null
  birthPlace: string | null
  birthCountry: string | null
  marriedName: string | null
  address: string | null
  addressComplement: string | null
  postalCode: string | null
  city: string | null
  country: string | null
  phoneNumber: string | null
  apiData: Json | null
  civility: User_civility | null
  siret: string | null
  legalForm: Company_legalForm | null
  login: string | null
  iban: string | null
  bankAccounts: Json | null
  cognitoSub: string | null
  isSubUserOfId: string | null
  externalId: string | null
}

export type UserActivity = {
  id: string
  createdAt: Date | null
  duration: number
  type: UserActivity_type | null
  userId: string
  stepCount: number | null
  learningObjectId: string | null
  learningModuleId: string | null
}

export type UserToken = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: UserToken_type | null
  token: Json | null
  userId: string | null
}

export type VatDeclaration = {
  id: string
  createdAt: Date | null
  type: VatDeclaration_type
  date: Date | null
  inputVatAmount: number | null
  outputVatAmount: number | null
  vatAmount: number | null
  importId: string | null
  accountingItemImportId: string | null
  companyId: string | null
  status: VatDeclaration_status | null
  data: Json | null
  endDate: Date | null
  mtaeStatus: string | null
  mtaeId: string | null
  lastExportDate: Date | null
  uniqueExportId: string | null
}

export type ZTillReceipt = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  date: Date | null
  period: ZTillReceipt_period | null
  netAmount: number | null
  vatAmount: number | null
  grossAmount: number | null
  importId: string | null
  documentId: string | null
  accountingItemImportId: string | null
  companyId: string | null
  originalDocumentId: string | null
  lastExportDate: Date | null
  uniqueExportId: string | null
}

export type BookletTemplate = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string | null
  accountingCompanyId: string
  content: Json | null
  companyScopeId: string | null
}

export type ExportTheme = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  theme: Json | null
  companyScopeId: string | null
  accountingCompanyId: string | null
}

export type ExportOffice = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  filename: string
  exports: Json | null
  companyScopeId: string | null
  fileId: string | null
}

export type LearningObject = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  description: string | null
  type: LearningObject_type | null
  status: LearningObject_status | null
  url: string | null
  embedCode: string | null
  path: string
  tags: string | null
  authorizedFor: string | null
  estimatedDuration: number | null
  stepCount: number | null
  key: string
  starterComponents: string | null
  mfe: string | null
}

export type LearningModule = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  description: string | null
  difficulty: number | null
  status: LearningObject_status | null
  profileType: Profile_type | null
  key: string
}

export type ImportMapping = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  companyId: string
  origin: string
  application: string
  type: ImportMapping_type | null
  externalId: string | null
  sourceName: string
  sourceDescription: string | null
  sourceSiret: string | null
  sourceIban: string | null
  accountableAccountId: string | null
  userId: string | null
  bankAccountId: string | null
  thirdPartyCompanyId: string | null
}

export type ExportMapping = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  companyId: string
  options: Json | null
}

export type ConnectorSetting = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string | null
  origin: string
  application: string
  applicationId: string
  companyId: string
  importMode: ImportMode_type | null
  startImportDate: Date | null
  data: Json | null
  status: ConnectorSetting_status
  isActive: boolean | null
  customerId: string | null
  currentError: string | null
  lastImportDate: Date | null
}

export type Extension = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string
  url: string | null
  type: Extension_type | null
  displayIn: Extension_displayIn | null
  companyScopeId: string | null
  companyId: string
}

export type Job = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  type: Job_type | null
  status: Job_status
  configuration: Json | null
  companyId: string
  data: Json | null
  errors: Json | null
  step: number
  stepCount: number
  mainJobId: string | null
}

export type Invoice = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  title: string | null
  additionalComment: string | null
  internalComment: string | null
  receiver: Json | null
  type: Invoice_type | null
  status: Invoice_status | null
  number: string | null
  periodicity: Invoice_periodicity | null
  autoValidation: boolean | null
  sendEmailAfterValidation: boolean | null
  nextDate: Date | null
  startDate: Date | null
  endDate: Date | null
  dueDateUnitOfTime: Invoice_dueDateUnitOfTime | null
  dueDateAmount: number | null
  date: Date | null
  dueDate: Date | null
  paymentTerms: Invoice_paymentTerms | null
  paymentDetails: string | null
  legalText: string | null
  vatType: VAT_type | null
  netAmount: number | null
  grossAmount: number | null
  vatAmount: number | null
  companyId: string | null
  creditNoteOfId: string | null
  clientId: string | null
  reccurenceInvoiceId: string | null
  depositOfId: string | null
  replacementOfId: string | null
  alreadySentByMail: boolean | null
  receiverEmail: string | null
  receivers: Json | null
  signatureId: string | null
  data: Json | null
  createStockOperations: boolean | null
  ownerId: string | null
  senderId: string | null
  discountPercentage: number | null
  discount: number | null
}

export type InvoiceLine = {
  id: string
  reference: string | null
  title: string | null
  description: string | null
  type: InvoiceLine_type
  quantity: number | null
  unitPrice: number | null
  netAmount: number | null
  grossAmount: number | null
  vatAmount: number | null
  vatRate: number | null
  invoiceId: string | null
  productId: string | null
  engagementLetterId: string | null
  discountPercentage: number | null
  discount: number | null
  isOptional: boolean
}

export type InvoiceProduct = {
  id: string
  reference: string | null
  title: string | null
  description: string | null
  outDated: boolean
  unitPrice: number | null
  vatRate: number | null
  companyId: string | null
  thirdPartyCompanyId: string | null
  percentage: number | null
  accountableAccountId: string | null
  stock: boolean | null
  family: string | null
  data: Json | null
  createdAt: Date | null
  updatedAt: Date | null
  usedInInvoiceMatrix: boolean | null
  type: InvoiceProduct_type | null
  tagId: string | null
  quantityType: InvoiceProduct_quantityType | null
  unitQuantity: number
  quantityName: string | null
}

export type InvoiceTheme = {
  id: string
  name: string | null
  invoiceType: InvoiceTheme_scope
  createdAt: Date | null
  updatedAt: Date | null
  companyId: string
  theme: Json | null
  backgroundId: string | null
  additionalDocumentId: string | null
}

export type StockInventory = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  date: Date | null
  status: StockInventory_status | null
  signerId: string | null
  accountingClosingId: string | null
  accountingPeriodId: string | null
  companyId: string | null
}

export type StockInventoryLine = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  quantity: number | null
  storedAmountSigned: number | null
  depreciatedAmountSigned: number | null
  unregisteredStockProduct: Json | null
  stockProductId: string | null
  accountableAccountId: string | null
  inventoryChangeId: string | null
  inventoryId: string | null
}

export type StockOperation = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  date: Date | null
  price: number | null
  unitPrice: number | null
  type: StockOperation_type | null
  quantity: number | null
  productQuantity: number | null
  data: Json | null
  salesPrice: number | null
  indirectExpense: number
  stockProductId: string | null
  companyId: string | null
  family: string | null
  documentId: string | null
  accountingLineId: string | null
  parentId: string | null
  productFamilyId: string | null
  inventoryId: string | null
}

export type ResourceAllocation = {
  id: string
  createdAt: Date | null
  updatedAt: Date | null
  name: string | null
  startDate: Date | null
  endDate: Date | null
  companyId: string
  productTagId: string
  yield: number | null
  resourceQuantityType: InvoiceProduct_quantityType | null
  resourceUnitQuantity: number
  resourceQuantity: number
  resourceQuantityName: string | null
  productQuantityType: InvoiceProduct_quantityType | null
  productUnitQuantity: number
  productQuantityName: string | null
}

export const Job_type = {
  MAIN: 'MAIN',
  DUPLICATE_COMPANY: 'DUPLICATE_COMPANY',
  DELETE_COMPANY: 'DELETE_COMPANY',
  COMPUTE_CARRY_FORWARD: 'COMPUTE_CARRY_FORWARD',
  CLOSE_ACCOUNTING_PERIOD: 'CLOSE_ACCOUNTING_PERIOD',
  FORECAST_6_7: 'FORECAST_6_7',
  FORECAST_CASH_FLOW: 'FORECAST_CASH_FLOW',
  CHECK_BALANCE: 'CHECK_BALANCE',
};

export type Job_type = (typeof Job_type)[keyof typeof Job_type]

export const Job_status = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  DONE: 'DONE',
  ERROR: 'ERROR',
};

export type Job_status = (typeof Job_status)[keyof typeof Job_status]

export const Extension_type = {
  CARD: 'CARD',
  FULL_PAGE: 'FULL_PAGE',
  DIALOG: 'DIALOG',
};

export type Extension_type = (typeof Extension_type)[keyof typeof Extension_type]

export const Offer_type = {
  TRAINING: 'TRAINING',
  ACCOUNTING_STARTER: 'ACCOUNTING_STARTER',
  ACCOUNTING_BASIC: 'ACCOUNTING_BASIC',
  ACCOUNTING_ESSENTIAL: 'ACCOUNTING_ESSENTIAL',
  ACCOUNTING_PREMIUM: 'ACCOUNTING_PREMIUM',
  BUSINESS_BASIC: 'BUSINESS_BASIC',
  BUSINESS_SMART: 'BUSINESS_SMART',
  BUSINESS_ESSENTIAL: 'BUSINESS_ESSENTIAL',
  BUSINESS_PREMIUM: 'BUSINESS_PREMIUM',
};

export type Offer_type = (typeof Offer_type)[keyof typeof Offer_type]

export const Extension_displayIn = {
  DOCUMENTS_TABLE: 'DOCUMENTS_TABLE',
  DASHBOARD: 'DASHBOARD',
  GENERAL_LEDGER: 'GENERAL_LEDGER',
  EXPORT: 'EXPORT',
};

export type Extension_displayIn = (typeof Extension_displayIn)[keyof typeof Extension_displayIn]

export const ImportMapping_type = {
  USER: 'USER',
  ACCOUNTABLE_ACCOUNT: 'ACCOUNTABLE_ACCOUNT',
  THIRD_PARTY_COMPANY: 'THIRD_PARTY_COMPANY',
  BANK_ACCOUNT: 'BANK_ACCOUNT',
};

export type ImportMapping_type = (typeof ImportMapping_type)[keyof typeof ImportMapping_type]

export const ImportMode_type = {
  WEBHOOK: 'WEBHOOK',
  PULL: 'PULL',
  PUSH: 'PUSH',
};

export type ImportMode_type = (typeof ImportMode_type)[keyof typeof ImportMode_type]

export const AccountableAccount_type = {
  Internal: 'Internal',
  Capital: 'Capital',
  FixedAssets: 'FixedAssets',
  Stocks: 'Stocks',
  Sales: 'Sales',
  Purchase: 'Purchase',
  ThirdParty: 'ThirdParty',
  Financial: 'Financial',
  VAT: 'VAT',
  Special: 'Special',
};

export type AccountableAccount_type = (typeof AccountableAccount_type)[keyof typeof AccountableAccount_type]

export const AccountingItemExport_status = {
  RUNNING_EXPORT: 'RUNNING_EXPORT',
  EXPORTED: 'EXPORTED',
  EXPORTED_WITH_ERRORS: 'EXPORTED_WITH_ERRORS',
};

export type AccountingItemExport_status = (typeof AccountingItemExport_status)[keyof typeof AccountingItemExport_status]

export const AccountingItemExport_type = {
  FEC: 'FEC',
  GENERAL_LEDGER: 'GENERAL_LEDGER',
};

export type AccountingItemExport_type = (typeof AccountingItemExport_type)[keyof typeof AccountingItemExport_type]

export const AccountingItemImport_status = {
  EDITION: 'EDITION',
  RUNNING_IMPORT: 'RUNNING_IMPORT',
  IMPORTED: 'IMPORTED',
  IMPORTED_WITH_ERRORS: 'IMPORTED_WITH_ERRORS',
  RUNNING_DELETE: 'RUNNING_DELETE',
  REJECTED: 'REJECTED',
};

export type AccountingItemImport_status = (typeof AccountingItemImport_status)[keyof typeof AccountingItemImport_status]

export const AccountingItemImport_type = {
  CONNECTOR: 'CONNECTOR',
  STANDARD: 'STANDARD',
  SYLAE: 'SYLAE',
  EBICS_JE_DECLARE: 'EBICS_JE_DECLARE',
  EBICS_SO_BANK: 'EBICS_SO_BANK',
  EBICS_MANUAL: 'EBICS_MANUAL',
};

export type AccountingItemImport_type = (typeof AccountingItemImport_type)[keyof typeof AccountingItemImport_type]

export const AccountingLine_type = {
  CARRY_FORWARD: 'CARRY_FORWARD',
  BUDGET: 'BUDGET',
  MANAGEMENT: 'MANAGEMENT',
  ACCOUNTING: 'ACCOUNTING',
  MANAGACCOU: 'MANAGACCOU',
  ORIGINAL: 'ORIGINAL',
};

export type AccountingLine_type = (typeof AccountingLine_type)[keyof typeof AccountingLine_type]

export const AuditCheck_type = {
  INFORM: 'INFORM',
  ACTION: 'ACTION',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  ACCOUNTING_REVIEW_AUTO: 'ACCOUNTING_REVIEW_AUTO',
  ACCOUNTING_REVIEW_MANUAL: 'ACCOUNTING_REVIEW_MANUAL',
  ACCOUNTING_REVIEW_MANUAL_CUSTOM: 'ACCOUNTING_REVIEW_MANUAL_CUSTOM',
};

export type AuditCheck_type = (typeof AuditCheck_type)[keyof typeof AuditCheck_type]

export const AuditCheck_status = {
  AVAILABLE: 'AVAILABLE',
  OBSOLETE: 'OBSOLETE',
};

export type AuditCheck_status = (typeof AuditCheck_status)[keyof typeof AuditCheck_status]

export const AuditCheckResult_status = {
  UNKNOWN: 'UNKNOWN',
  IGNORE: 'IGNORE',
  TO_UPDATE: 'TO_UPDATE',
  EDITION: 'EDITION',
  VALIDATED: 'VALIDATED',
};

export type AuditCheckResult_status = (typeof AuditCheckResult_status)[keyof typeof AuditCheckResult_status]

export const AuditCheckResult_type = {
  IN_AUDIT_REPORT: 'IN_AUDIT_REPORT',
  NOT_IN_AUDIT_REPORT: 'NOT_IN_AUDIT_REPORT',
};

export type AuditCheckResult_type = (typeof AuditCheckResult_type)[keyof typeof AuditCheckResult_type]

export const BankAccount_type = {
  BANK: 'BANK',
  CASH_BOX: 'CASH_BOX',
};

export type BankAccount_type = (typeof BankAccount_type)[keyof typeof BankAccount_type]

export const BankAccount_currency = {
  EUR: 'EUR',
  DOLLAR: 'DOLLAR',
};

export type BankAccount_currency = (typeof BankAccount_currency)[keyof typeof BankAccount_currency]

export const BankAccountBalance_type = {
  BUDGET_INSIGHT: 'BUDGET_INSIGHT',
  EBICS_JE_DECLARE: 'EBICS_JE_DECLARE',
  EBICS_SO_BANK: 'EBICS_SO_BANK',
  EBICS_MANUAL: 'EBICS_MANUAL',
  BANK_STATEMENT: 'BANK_STATEMENT',
  MANUAL: 'MANUAL',
  IMPORT_TXT: 'IMPORT_TXT',
  IMPORT_FEC: 'IMPORT_FEC',
  COMPUTE_CARRY_FORWARD: 'COMPUTE_CARRY_FORWARD',
};

export type BankAccountBalance_type = (typeof BankAccountBalance_type)[keyof typeof BankAccountBalance_type]

export const BankTransaction_status = {
  UPLOADED: 'UPLOADED',
  ANALYSED: 'ANALYSED',
  VALIDATED: 'VALIDATED',
  INFORMATION_REQUEST: 'INFORMATION_REQUEST',
  DELETED: 'DELETED',
};

export type BankTransaction_status = (typeof BankTransaction_status)[keyof typeof BankTransaction_status]

export const BankTransaction_transactionType = {
  UNKNOWN: 'UNKNOWN',
  CREDIT_CARD: 'CREDIT_CARD',
  CASH: 'CASH',
  CHEQUE: 'CHEQUE',
  TRANSFER: 'TRANSFER',
  LUNCHEON_VOUCHER: 'LUNCHEON_VOUCHER',
  HOLIDAY_VOUCHER: 'HOLIDAY_VOUCHER',
  PAYPAL: 'PAYPAL',
  DISTRIBUTION_PLATFORM: 'DISTRIBUTION_PLATFORM',
  CREDIT_NOTES_ISSUED: 'CREDIT_NOTES_ISSUED',
  CREDIT_NOTES_RECOVERY: 'CREDIT_NOTES_RECOVERY',
  BANK_CHARGE: 'BANK_CHARGE',
  DIRECT_DEBIT: 'DIRECT_DEBIT',
  DEPOSIT: 'DEPOSIT',
  FREE: 'FREE',
};

export type BankTransaction_transactionType = (typeof BankTransaction_transactionType)[keyof typeof BankTransaction_transactionType]

export const BankTransactionWordingPattern_scopeSharingStatus = {
  PENDING_VALIDATION: 'PENDING_VALIDATION',
  VALIDATE: 'VALIDATE',
};

export type BankTransactionWordingPattern_scopeSharingStatus = (typeof BankTransactionWordingPattern_scopeSharingStatus)[keyof typeof BankTransactionWordingPattern_scopeSharingStatus]

export const Budget_type = {
  IMPORT: 'IMPORT',
  FORM: 'FORM',
  MANUAL: 'MANUAL',
  FORM_FULL: 'FORM_FULL',
};

export type Budget_type = (typeof Budget_type)[keyof typeof Budget_type]

export const BudgetFormLine_type = {
  CAPITAL_CONTRIBUTION: 'CAPITAL_CONTRIBUTION',
  CURRENT_ACCOUNT_CONTRIBUTION: 'CURRENT_ACCOUNT_CONTRIBUTION',
  LOAN: 'LOAN',
  INVESTMENT_GRANT: 'INVESTMENT_GRANT',
  ACTIVITY: 'ACTIVITY',
  RECURRING_CHARGE: 'RECURRING_CHARGE',
  UNIQUE_CHARGE: 'UNIQUE_CHARGE',
  STAFF: 'STAFF',
};

export type BudgetFormLine_type = (typeof BudgetFormLine_type)[keyof typeof BudgetFormLine_type]

export const Chat_type = {
  OBJECT: 'OBJECT',
  COMPANY: 'COMPANY',
};

export type Chat_type = (typeof Chat_type)[keyof typeof Chat_type]

export const ChatMessage_smartAsk = {
  VAT_AMOUNT: 'VAT_AMOUNT',
  VAT_RATE: 'VAT_RATE',
  HT: 'HT',
  TTC: 'TTC',
  THIRD_PARTY_COMPANY: 'THIRD_PARTY_COMPANY',
  DATE: 'DATE',
  DUE_DATE: 'DUE_DATE',
  INVOICE_NUMBER: 'INVOICE_NUMBER',
  LOW_QUALITY: 'LOW_QUALITY',
};

export type ChatMessage_smartAsk = (typeof ChatMessage_smartAsk)[keyof typeof ChatMessage_smartAsk]

export const Company_type = {
  NORMAL: 'NORMAL',
  GENERIC: 'GENERIC',
  CONSUMER: 'CONSUMER',
};

export type Company_type = (typeof Company_type)[keyof typeof Company_type]

export const Company_solution = {
  NO_SOLUTION: 'NO_SOLUTION',
  ACCOUNTING: 'ACCOUNTING',
  MANAGEMENT: 'MANAGEMENT',
};

export type Company_solution = (typeof Company_solution)[keyof typeof Company_solution]

export const Company_legalForm = {
  ASSO: 'ASSO',
  CE: 'CE',
  CL: 'CL',
  CNE: 'CNE',
  COOP: 'COOP',
  CUMA: 'CUMA',
  DPT: 'DPT',
  EARL: 'EARL',
  EC: 'EC',
  EI: 'EI',
  EIRL: 'EIRL',
  ETRANGER: 'ETRANGER',
  EURL: 'EURL',
  FEDE: 'FEDE',
  FOND: 'FOND',
  GAEC: 'GAEC',
  GEIE: 'GEIE',
  GF: 'GF',
  GFA: 'GFA',
  GFR: 'GFR',
  GIE: 'GIE',
  GPT: 'GPT',
  GREFFE: 'GREFFE',
  IND: 'IND',
  MUT: 'MUT',
  SA: 'SA',
  SAE: 'SAE',
  SARL: 'SARL',
  SAS: 'SAS',
  SASU: 'SASU',
  SC: 'SC',
  SCA: 'SCA',
  SCCV: 'SCCV',
  SCE: 'SCE',
  SCEA: 'SCEA',
  SCEV: 'SCEV',
  SCH: 'SCH',
  SCI: 'SCI',
  SCM: 'SCM',
  SCP: 'SCP',
  SCPG: 'SCPG',
  SCPI: 'SCPI',
  SCS: 'SCS',
  SCVV: 'SCVV',
  SDE: 'SDE',
  SELARL: 'SELARL',
  SNC: 'SNC',
};

export type Company_legalForm = (typeof Company_legalForm)[keyof typeof Company_legalForm]

export const Company_vatSystem = {
  NotSubjectTo: 'NotSubjectTo',
  BelowTheThreshold: 'BelowTheThreshold',
  Normal: 'Normal',
  Simplified: 'Simplified',
  SimplifiedExercise: 'SimplifiedExercise',
  Quartely: 'Quartely',
  FlatRateRefund: 'FlatRateRefund',
};

export type Company_vatSystem = (typeof Company_vatSystem)[keyof typeof Company_vatSystem]

export const Company_vatOption = {
  Debits: 'Debits',
  Receipts: 'Receipts',
};

export type Company_vatOption = (typeof Company_vatOption)[keyof typeof Company_vatOption]

export const Company_taxSystem = {
  BIC_Normal: 'BIC_Normal',
  BIC_Simplified: 'BIC_Simplified',
  BNC: 'BNC',
  RF: 'RF',
};

export type Company_taxSystem = (typeof Company_taxSystem)[keyof typeof Company_taxSystem]

export const Company_incomeCategory = {
  ASSOC: 'ASSOC',
  BA: 'BA',
  BIC: 'BIC',
  BNC: 'BNC',
  RF: 'RF',
};

export type Company_incomeCategory = (typeof Company_incomeCategory)[keyof typeof Company_incomeCategory]

export const Company_taxRegime = {
  OTHER: 'OTHER',
  SELF_EMPLOYED: 'SELF_EMPLOYED',
  CONTROLLED_DECLARATION: 'CONTROLLED_DECLARATION',
  MICRO_BA: 'MICRO_BA',
  MICRO_ENTERPRISE: 'MICRO_ENTERPRISE',
  REAL_SIMPLIFIED: 'REAL_SIMPLIFIED',
  REAL_NORMAL: 'REAL_NORMAL',
};

export type Company_taxRegime = (typeof Company_taxRegime)[keyof typeof Company_taxRegime]

export const CompanyScope_System = {
  RS_IS: 'RS_IS',
  RS_IR: 'RS_IR',
  RN_IS: 'RN_IS',
  RN_IR: 'RN_IR',
  BNC: 'BNC',
  RF: 'RF',
};

export type CompanyScope_System = (typeof CompanyScope_System)[keyof typeof CompanyScope_System]

export const Company_invoiceNumbering = {
  CONTINUOUS: 'CONTINUOUS',
  MONTH: 'MONTH',
  NO_LOGIC: 'NO_LOGIC',
};

export type Company_invoiceNumbering = (typeof Company_invoiceNumbering)[keyof typeof Company_invoiceNumbering]

export const Company_taxSystemType = {
  IS: 'IS',
  IR: 'IR',
};

export type Company_taxSystemType = (typeof Company_taxSystemType)[keyof typeof Company_taxSystemType]

export const CompanyActivitySuggestion_status = {
  PENDING_VALIDATION: 'PENDING_VALIDATION',
  VALIDATED: 'VALIDATED',
  INVALIDATED: 'INVALIDATED',
};

export type CompanyActivitySuggestion_status = (typeof CompanyActivitySuggestion_status)[keyof typeof CompanyActivitySuggestion_status]

export const CompanyScope_scopeSharingStatus = {
  PENDING_VALIDATION: 'PENDING_VALIDATION',
  VALIDATE: 'VALIDATE',
};

export type CompanyScope_scopeSharingStatus = (typeof CompanyScope_scopeSharingStatus)[keyof typeof CompanyScope_scopeSharingStatus]

export const CompanySuggestion_status = {
  PENDING_VALIDATION: 'PENDING_VALIDATION',
  VALIDATED: 'VALIDATED',
  INVALIDATED: 'INVALIDATED',
};

export type CompanySuggestion_status = (typeof CompanySuggestion_status)[keyof typeof CompanySuggestion_status]

export const CompanyUser_role = {
  OWNER: 'OWNER',
};

export type CompanyUser_role = (typeof CompanyUser_role)[keyof typeof CompanyUser_role]

export const Declaration_status = {
  EDITION: 'EDITION',
  SENT: 'SENT',
  VALIDATED_NOT_SENT: 'VALIDATED_NOT_SENT',
  ERROR: 'ERROR',
};

export type Declaration_status = (typeof Declaration_status)[keyof typeof Declaration_status]

export const Declaration_type = {
  DECLARATION: 'DECLARATION',
  BUNDLE: 'BUNDLE',
};

export type Declaration_type = (typeof Declaration_type)[keyof typeof Declaration_type]

export const Document_type = {
  UNKNOWN: 'UNKNOWN',
  PURCHASE_INVOICE: 'PURCHASE_INVOICE',
  SALES_INVOICE: 'SALES_INVOICE',
  QUOTATION: 'QUOTATION',
  CURRENT_ACCOUNT: 'CURRENT_ACCOUNT',
  OPERATOR_ACCOUNT: 'OPERATOR_ACCOUNT',
  EXPENSE_REPORT: 'EXPENSE_REPORT',
  EXPENSE_REPORT_DOCUMENT: 'EXPENSE_REPORT_DOCUMENT',
  ZTILL_RECEIPT: 'ZTILL_RECEIPT',
  WITHOUT_INVOICE: 'WITHOUT_INVOICE',
  VAT: 'VAT',
  MISCELLANEOUS: 'MISCELLANEOUS',
  WITH_DELTA: 'WITH_DELTA',
  SALES_INVOICE_AUTO: 'SALES_INVOICE_AUTO',
  BANK_CHARGE: 'BANK_CHARGE',
  PAY_SLIP: 'PAY_SLIP',
  MISCELLANEOUS_OPERATION: 'MISCELLANEOUS_OPERATION',
  CARRY_FORWARD: 'CARRY_FORWARD',
  BANK_STATEMENT: 'BANK_STATEMENT',
  WAITING_CASHING: 'WAITING_CASHING',
  BANK_TRANSACTION: 'BANK_TRANSACTION',
  MISCELLANEOUS_SPLIT: 'MISCELLANEOUS_SPLIT',
  SEPA: 'SEPA',
  SUBSCRIPTION: 'SUBSCRIPTION',
  LOAN: 'LOAN',
  LEASING: 'LEASING',
  SUBSCRIPTION_ACCOUNTING: 'SUBSCRIPTION_ACCOUNTING',
  LOAN_ACCOUNTING: 'LOAN_ACCOUNTING',
  LEASING_ACCOUNTING: 'LEASING_ACCOUNTING',
  CAR_REGISTRATION: 'CAR_REGISTRATION',
  KBIS: 'KBIS',
};

export type Document_type = (typeof Document_type)[keyof typeof Document_type]

export const Document_status = {
  ORIGINAL_UPLOADED: 'ORIGINAL_UPLOADED',
  UPLOADED: 'UPLOADED',
  ANALYSED: 'ANALYSED',
  ANALYSED_OK: 'ANALYSED_OK',
  PENDING: 'PENDING',
  VALIDATED: 'VALIDATED',
  RECONCILED: 'RECONCILED',
  INFORMATION_REQUEST: 'INFORMATION_REQUEST',
  DENIED: 'DENIED',
  ERROR: 'ERROR',
  DELETED: 'DELETED',
};

export type Document_status = (typeof Document_status)[keyof typeof Document_status]

export const VAT_type = {
  NO_VAT_TYPE: 'NO_VAT_TYPE',
  INTRACOM: 'INTRACOM',
  INTRACOM_DEBIT: 'INTRACOM_DEBIT',
  INTRACOM_CREDIT: 'INTRACOM_CREDIT',
  IMPORT: 'IMPORT',
  IMPORT_DEBIT: 'IMPORT_DEBIT',
  IMPORT_CREDIT: 'IMPORT_CREDIT',
  EXPORT: 'EXPORT',
  REVERSE_CHARGE: 'REVERSE_CHARGE',
  REVERSE_CHARGE_DEBIT: 'REVERSE_CHARGE_DEBIT',
  REVERSE_CHARGE_CREDIT: 'REVERSE_CHARGE_CREDIT',
  ENERGY_DELIVERIES: 'ENERGY_DELIVERIES',
  OIL_PRODUCTS: 'OIL_PRODUCTS',
};

export type VAT_type = (typeof VAT_type)[keyof typeof VAT_type]

export const VAT_operationType = {
  PURCHASE: 'PURCHASE',
  PURCHASE_FIXED_ASSET: 'PURCHASE_FIXED_ASSET',
  SALE: 'SALE',
};

export type VAT_operationType = (typeof VAT_operationType)[keyof typeof VAT_operationType]

export const EngagementLetter_status = {
  DRAFT: 'DRAFT',
  VALIDATED: 'VALIDATED',
  BEING_SIGNED: 'BEING_SIGNED',
  SIGNED: 'SIGNED',
};

export type EngagementLetter_status = (typeof EngagementLetter_status)[keyof typeof EngagementLetter_status]

export const ExpenseReport_status = {
  EDITION: 'EDITION',
  PENDING_DOCUMENTS_VALIDATION: 'PENDING_DOCUMENTS_VALIDATION',
  PENDING_VALIDATION: 'PENDING_VALIDATION',
  VALIDATED: 'VALIDATED',
  DENIED: 'DENIED',
  INFORMATION_REQUEST: 'INFORMATION_REQUEST',
};

export type ExpenseReport_status = (typeof ExpenseReport_status)[keyof typeof ExpenseReport_status]

export const File_type = {
  PDF: 'PDF',
  PNG: 'PNG',
  JPEG: 'JPEG',
  JPG: 'JPG',
  TIFF: 'TIFF',
  TIF: 'TIF',
  CSV: 'CSV',
  TSV: 'TSV',
  TXT: 'TXT',
  DAT: 'DAT',
  XLS: 'XLS',
  XLSX: 'XLSX',
  DOCX: 'DOCX',
  JSON: 'JSON',
  ZIP: 'ZIP',
  PNM: 'PNM',
  XML: 'XML',
  TRA: 'TRA',
  OFX: 'OFX',
};

export type File_type = (typeof File_type)[keyof typeof File_type]

export const Mail_type = {
  UNKNOWN: 'UNKNOWN',
  OUTSTANDING: 'OUTSTANDING',
  SENT_INVOICE: 'SENT_INVOICE',
  SENT_GROUPED_INVOICES: 'SENT_GROUPED_INVOICES',
  DUN_REQUEST: 'DUN_REQUEST',
  DEFFERED_PAYMENT_REQUEST: 'DEFFERED_PAYMENT_REQUEST',
  EDI: 'EDI',
};

export type Mail_type = (typeof Mail_type)[keyof typeof Mail_type]

export const OperatingAccount_status = {
  FINISHED: 'FINISHED',
  UNFINISHED: 'UNFINISHED',
  EDITION: 'EDITION',
  VALIDATE: 'VALIDATE',
};

export type OperatingAccount_status = (typeof OperatingAccount_status)[keyof typeof OperatingAccount_status]

export const OperatingAccount_scopeSharingStatus = {
  PENDING_VALIDATION: 'PENDING_VALIDATION',
  VALIDATE: 'VALIDATE',
};

export type OperatingAccount_scopeSharingStatus = (typeof OperatingAccount_scopeSharingStatus)[keyof typeof OperatingAccount_scopeSharingStatus]

export const OperatingAccount_use = {
  OPERATING_ACCOUNT: 'OPERATING_ACCOUNT',
  CASH_FLOW_PLAN: 'CASH_FLOW_PLAN',
};

export type OperatingAccount_use = (typeof OperatingAccount_use)[keyof typeof OperatingAccount_use]

export const OperatingAccount_type = {
  SIG: 'SIG',
  FLASH_SITUATION: 'FLASH_SITUATION',
  INCOME_STATEMENT: 'INCOME_STATEMENT',
  BALANCE_SHEET: 'BALANCE_SHEET',
  MISCELLANEOUS: 'MISCELLANEOUS',
};

export type OperatingAccount_type = (typeof OperatingAccount_type)[keyof typeof OperatingAccount_type]

export const PartitionTag_type = {
  Partial: 'Partial',
  Total: 'Total',
  AccountableAccount: 'AccountableAccount',
  AccessRight: 'AccessRight',
  Building: 'Building',
  AccountingFirm: 'AccountingFirm',
};

export type PartitionTag_type = (typeof PartitionTag_type)[keyof typeof PartitionTag_type]

export const PartitionTag_scope = {
  UNKNOWN: 'UNKNOWN',
  EXPENSE: 'EXPENSE',
  INCOME: 'INCOME',
  EXPENSE_INCOME: 'EXPENSE_INCOME',
};

export type PartitionTag_scope = (typeof PartitionTag_scope)[keyof typeof PartitionTag_scope]

export const Payment_type = {
  UNKNOWN: 'UNKNOWN',
  PAYMENT_CREDIT: 'PAYMENT_CREDIT',
  SUPPLIER: 'SUPPLIER',
  CUSTOMER: 'CUSTOMER',
  EXPENSE_REPORT: 'EXPENSE_REPORT',
  SALARY: 'SALARY',
};

export type Payment_type = (typeof Payment_type)[keyof typeof Payment_type]

export const Payment_status = {
  CREATED: 'CREATED',
  VALIDATING: 'VALIDATING',
  ERROR: 'ERROR',
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
  DONE: 'DONE',
  EXPIRED: 'EXPIRED',
  ACCEPTED: 'ACCEPTED',
  ACCEPTED_NO_BANK_STATUS: 'ACCEPTED_NO_BANK_STATUS',
};

export type Payment_status = (typeof Payment_status)[keyof typeof Payment_status]

export const ProfileCompany_accountingType = {
  RECEIVABLES_DEBT: 'RECEIVABLES_DEBT',
  INCOMING_OUTGOING_PAYMENT: 'INCOMING_OUTGOING_PAYMENT',
};

export type ProfileCompany_accountingType = (typeof ProfileCompany_accountingType)[keyof typeof ProfileCompany_accountingType]

export const Reconciliation_status = {
  EDITION: 'EDITION',
  PENDING_VALIDATION: 'PENDING_VALIDATION',
  PENDING_VALIDATION_FROM_CLIENT: 'PENDING_VALIDATION_FROM_CLIENT',
  VALIDATED: 'VALIDATED',
  EXPORTED: 'EXPORTED',
  INFORMATION_REQUEST: 'INFORMATION_REQUEST',
};

export type Reconciliation_status = (typeof Reconciliation_status)[keyof typeof Reconciliation_status]

export const Reconciliation_type = {
  DEFAULT: 'DEFAULT',
  ZTILL_RECEIPT: 'ZTILL_RECEIPT',
  CURRENT_ACCOUNT: 'CURRENT_ACCOUNT',
  OPERATOR_ACCOUNT: 'OPERATOR_ACCOUNT',
  CASH_BOX: 'CASH_BOX',
  DONE: 'DONE',
};

export type Reconciliation_type = (typeof Reconciliation_type)[keyof typeof Reconciliation_type]

export const Reconciliation_toReplaceDocumentType = {
  UNKNOWN: 'UNKNOWN',
  PURCHASE_INVOICE: 'PURCHASE_INVOICE',
  SALES_INVOICE: 'SALES_INVOICE',
  CURRENT_ACCOUNT: 'CURRENT_ACCOUNT',
  OPERATOR_ACCOUNT: 'OPERATOR_ACCOUNT',
  EXPENSE_REPORT: 'EXPENSE_REPORT',
  EXPENSE_REPORT_DOCUMENT: 'EXPENSE_REPORT_DOCUMENT',
  ZTILL_RECEIPT: 'ZTILL_RECEIPT',
  WITHOUT_INVOICE: 'WITHOUT_INVOICE',
  VAT: 'VAT',
  MISCELLANEOUS: 'MISCELLANEOUS',
  WITH_DELTA: 'WITH_DELTA',
  SALES_INVOICE_AUTO: 'SALES_INVOICE_AUTO',
  BANK_CHARGE: 'BANK_CHARGE',
  PAY_SLIP: 'PAY_SLIP',
  MISCELLANEOUS_OPERATION: 'MISCELLANEOUS_OPERATION',
  CARRY_FORWARD: 'CARRY_FORWARD',
  BANK_STATEMENT: 'BANK_STATEMENT',
  PENDING: 'PENDING',
  WAITING_CASHING: 'WAITING_CASHING',
  BANK_TRANSACTION: 'BANK_TRANSACTION',
};

export type Reconciliation_toReplaceDocumentType = (typeof Reconciliation_toReplaceDocumentType)[keyof typeof Reconciliation_toReplaceDocumentType]

export const Tag_type = {
  SubledgerAccount: 'SubledgerAccount',
  AccountableAccount: 'AccountableAccount',
  SubAccountableAccount: 'SubAccountableAccount',
  External: 'External',
  Internal: 'Internal',
  TagDistribution: 'TagDistribution',
};

export type Tag_type = (typeof Tag_type)[keyof typeof Tag_type]

export const Task_periodicity = {
  WEEKLY: 'WEEKLY',
  BIMONTHLY: 'BIMONTHLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  BIANNUAL: 'BIANNUAL',
  ANNUAL: 'ANNUAL',
};

export type Task_periodicity = (typeof Task_periodicity)[keyof typeof Task_periodicity]

export const InvoiceProduct_quantityType = {
  QUANTITY: 'QUANTITY',
  MASS: 'MASS',
  LENGTH: 'LENGTH',
  AREA: 'AREA',
  VOLUME: 'VOLUME',
};

export type InvoiceProduct_quantityType = (typeof InvoiceProduct_quantityType)[keyof typeof InvoiceProduct_quantityType]

export const Task_type = {
  MISCELLANEOUS: 'MISCELLANEOUS',
  VAT: 'VAT',
  IS: 'IS',
};

export type Task_type = (typeof Task_type)[keyof typeof Task_type]

export const Task_status = {
  TO_BE_DONE: 'TO_BE_DONE',
  DONE: 'DONE',
};

export type Task_status = (typeof Task_status)[keyof typeof Task_status]

export const User_type = {
  NATURAL_PERSON: 'NATURAL_PERSON',
  LEGAL_PERSON: 'LEGAL_PERSON',
  API_USER: 'API_USER',
};

export type User_type = (typeof User_type)[keyof typeof User_type]

export const User_civility = {
  MR: 'MR',
  MS: 'MS',
};

export type User_civility = (typeof User_civility)[keyof typeof User_civility]

export const UserActivity_action = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  VALIDATE: 'VALIDATE',
  REVIEW: 'REVIEW',
  DELETE: 'DELETE',
  RESTORE: 'RESTORE',
  TAG: 'TAG',
};

export type UserActivity_action = (typeof UserActivity_action)[keyof typeof UserActivity_action]

export const UserActivity_type = {
  LEARNING: 'LEARNING',
  FEATURE_UPDATE: 'FEATURE_UPDATE',
};

export type UserActivity_type = (typeof UserActivity_type)[keyof typeof UserActivity_type]

export const UserToken_type = {
  BUDGET_INSIGHT: 'BUDGET_INSIGHT',
  MANAGER_ONE: 'MANAGER_ONE',
  PUBLIC_BOBBEE_API: 'PUBLIC_BOBBEE_API',
};

export type UserToken_type = (typeof UserToken_type)[keyof typeof UserToken_type]

export const VatDeclaration_type = {
  DEFAULT: 'DEFAULT',
  DONE: 'DONE',
  AUTO: 'AUTO',
  MANUAL: 'MANUAL',
  MANUAL_VAT_REPORT: 'MANUAL_VAT_REPORT',
  AUTO_VAT_REPORT: 'AUTO_VAT_REPORT',
  IMPORT: 'IMPORT',
};

export type VatDeclaration_type = (typeof VatDeclaration_type)[keyof typeof VatDeclaration_type]

export const VatDeclaration_status = {
  TO_BE_SENT: 'TO_BE_SENT',
  SEND: 'SEND',
};

export type VatDeclaration_status = (typeof VatDeclaration_status)[keyof typeof VatDeclaration_status]

export const Tax_type = {
  VAT: 'VAT',
  DAS2: 'DAS2',
};

export type Tax_type = (typeof Tax_type)[keyof typeof Tax_type]

export const ZTillReceipt_period = {
  UNKNOWN: 'UNKNOWN',
  AM: 'AM',
  PM: 'PM',
  DA: 'DA',
  WE: 'WE',
  MO: 'MO',
};

export type ZTillReceipt_period = (typeof ZTillReceipt_period)[keyof typeof ZTillReceipt_period]

export const DeclarationForm_type = {
  VERSION1: 'VERSION1',
  VERSION2: 'VERSION2',
};

export type DeclarationForm_type = (typeof DeclarationForm_type)[keyof typeof DeclarationForm_type]

export const DeclarationForm_status = {
  VALIDATED: 'VALIDATED',
  EDITION: 'EDITION',
  PENDING_VALIDATION: 'PENDING_VALIDATION',
};

export type DeclarationForm_status = (typeof DeclarationForm_status)[keyof typeof DeclarationForm_status]

export const LearningObject_type = {
  IFRAM: 'IFRAM',
  VIDEO: 'VIDEO',
  DOCUMENT: 'DOCUMENT',
};

export type LearningObject_type = (typeof LearningObject_type)[keyof typeof LearningObject_type]

export const LearningObject_status = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
};

export type LearningObject_status = (typeof LearningObject_status)[keyof typeof LearningObject_status]

export const Company_clientUserAccessRight = {
  AVAILABLE_COMPANY: 'AVAILABLE_COMPANY',
  AVAILABLE_WORKSPACE: 'AVAILABLE_WORKSPACE',
};

export type Company_clientUserAccessRight = (typeof Company_clientUserAccessRight)[keyof typeof Company_clientUserAccessRight]

export const Profile_type = {
  ROOT: 'ROOT',
  ACCOUNTING: 'ACCOUNTING',
  ACCOUNTING_CLIENT: 'ACCOUNTING_CLIENT',
  BUSINESS: 'BUSINESS',
};

export type Profile_type = (typeof Profile_type)[keyof typeof Profile_type]

export const BobbeePriceList_type = {
  STANDARD: 'STANDARD',
  SPECIFIC: 'SPECIFIC',
};

export type BobbeePriceList_type = (typeof BobbeePriceList_type)[keyof typeof BobbeePriceList_type]

export const BobbeePriceList_scope = {
  EXPERT: 'EXPERT',
  BUSINESS: 'BUSINESS',
};

export type BobbeePriceList_scope = (typeof BobbeePriceList_scope)[keyof typeof BobbeePriceList_scope]

export const ConnectorSetting_status = {
  CONNECTING: 'CONNECTING',
  ACTIVE: 'ACTIVE',
  ERROR: 'ERROR',
};

export type ConnectorSetting_status = (typeof ConnectorSetting_status)[keyof typeof ConnectorSetting_status]

export const Company_thirdPartyCompanyPaymentType = {
  TRANSFER: 'TRANSFER',
  CHEQUE: 'CHEQUE',
  DIRECT_DEBIT: 'DIRECT_DEBIT',
};

export type Company_thirdPartyCompanyPaymentType = (typeof Company_thirdPartyCompanyPaymentType)[keyof typeof Company_thirdPartyCompanyPaymentType]

export const CompanyUser_companyOwnerType = {
  SHAREHOLDER: 'SHAREHOLDER',
  ASSOCIATE: 'ASSOCIATE',
};

export type CompanyUser_companyOwnerType = (typeof CompanyUser_companyOwnerType)[keyof typeof CompanyUser_companyOwnerType]

export const CompanyUser_legalRepresentativeType = {
  CEO: 'CEO',
  MANAGING_DIRECTOR: 'MANAGING_DIRECTOR',
  PRESIDENT: 'PRESIDENT',
  SUPERVISOR: 'SUPERVISOR',
  GENERAL_SUPERVISOR: 'GENERAL_SUPERVISOR',
};

export type CompanyUser_legalRepresentativeType = (typeof CompanyUser_legalRepresentativeType)[keyof typeof CompanyUser_legalRepresentativeType]

export const Company_rfRevenueType = {
  BA: 'BA',
  BIC_IS: 'BIC_IS',
};

export type Company_rfRevenueType = (typeof Company_rfRevenueType)[keyof typeof Company_rfRevenueType]

export const User_SubscriptionType = {
  EDI_TVA: 'EDI_TVA',
  EDI_IS: 'EDI_IS',
  EDI_CVAE: 'EDI_CVAE',
  EDI_RCM: 'EDI_RCM',
  EDI_IFU: 'EDI_IFU',
  EDI_DAS2: 'EDI_DAS2',
  EDI_DECLOYER: 'EDI_DECLOYER',
  EDI_LIASSE: 'EDI_LIASSE',
  BROKEN_BANKACCOUNT: 'BROKEN_BANKACCOUNT',
};

export type User_SubscriptionType = (typeof User_SubscriptionType)[keyof typeof User_SubscriptionType]

export const Invoice_type = {
  INVOICE: 'INVOICE',
  INVOICE_RECURRENT: 'INVOICE_RECURRENT',
  INVOICE_MATRIX: 'INVOICE_MATRIX',
  CREDIT_NOTE: 'CREDIT_NOTE',
  QUOTATION: 'QUOTATION',
  BILL_DELIVERY: 'BILL_DELIVERY',
};

export type Invoice_type = (typeof Invoice_type)[keyof typeof Invoice_type]

export const InvoiceTheme_scope = {
  ALL: 'ALL',
  INVOICE: 'INVOICE',
  CREDIT_NOTE: 'CREDIT_NOTE',
  QUOTATION: 'QUOTATION',
  BILL_DELIVERY: 'BILL_DELIVERY',
};

export type InvoiceTheme_scope = (typeof InvoiceTheme_scope)[keyof typeof InvoiceTheme_scope]

export const Invoice_status = {
  DRAFT: 'DRAFT',
  BEING_VALIDATED: 'BEING_VALIDATED',
  VALIDATED: 'VALIDATED',
  BEING_SIGNED: 'BEING_SIGNED',
  SIGNED: 'SIGNED',
  ACCEPTED: 'ACCEPTED',
  PAID: 'PAID',
  DELETED: 'DELETED',
  DENIED: 'DENIED',
};

export type Invoice_status = (typeof Invoice_status)[keyof typeof Invoice_status]

export const Invoice_periodicity = {
  WEEKLY: 'WEEKLY',
  BIMONTHLY: 'BIMONTHLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  BIANNUAL: 'BIANNUAL',
  ANNUAL: 'ANNUAL',
};

export type Invoice_periodicity = (typeof Invoice_periodicity)[keyof typeof Invoice_periodicity]

export const Invoice_dueDateUnitOfTime = {
  DAYS: 'DAYS',
  WEEKS: 'WEEKS',
  MONTHS: 'MONTHS',
};

export type Invoice_dueDateUnitOfTime = (typeof Invoice_dueDateUnitOfTime)[keyof typeof Invoice_dueDateUnitOfTime]

export const Invoice_paymentTerms = {
  WITH_ORDER: 'WITH_ORDER',
  DAYS_30: 'DAYS_30',
  DAYS_45: 'DAYS_45',
  DAYS_60: 'DAYS_60',
  DAYS_30_END_MONTH: 'DAYS_30_END_MONTH',
  DAYS_45_END_MONTH: 'DAYS_45_END_MONTH',
};

export type Invoice_paymentTerms = (typeof Invoice_paymentTerms)[keyof typeof Invoice_paymentTerms]

export const InvoiceLine_type = {
  PRODUCT: 'PRODUCT',
  TOTAL: 'TOTAL',
  DEPOSIT: 'DEPOSIT',
};

export type InvoiceLine_type = (typeof InvoiceLine_type)[keyof typeof InvoiceLine_type]

export const InvoiceProduct_type = {
  EXPENSE: 'EXPENSE',
  EXPENSE_DIRECT: 'EXPENSE_DIRECT',
  EXPENSE_INDIRECT_PRIMARY_ON_EXPENSES: 'EXPENSE_INDIRECT_PRIMARY_ON_EXPENSES',
  EXPENSE_INDIRECT_PRIMARY_ON_PRODUCTS: 'EXPENSE_INDIRECT_PRIMARY_ON_PRODUCTS',
  EXPENSE_INDIRECT_SECONDARY: 'EXPENSE_INDIRECT_SECONDARY',
  PRODUCT: 'PRODUCT',
};

export type InvoiceProduct_type = (typeof InvoiceProduct_type)[keyof typeof InvoiceProduct_type]

export const StockInventory_status = {
  EDITION: 'EDITION',
  VALIDATED: 'VALIDATED',
};

export type StockInventory_status = (typeof StockInventory_status)[keyof typeof StockInventory_status]

export const StockOperation_type = {
  INPUT: 'INPUT',
  OUTPUT: 'OUTPUT',
};

export type StockOperation_type = (typeof StockOperation_type)[keyof typeof StockOperation_type]

